/**
 * Глобальные настройки, разбитые по секциям. Каждая секция хранится отдельной строкой
 * в tracker.settings и валидируется своей zod-схемой. Значения по умолчанию —
 * «заводские», в т.ч. правила, перенесённые из прототипа bs-zoka.
 */
import { z } from 'zod';
import { autoreplySchema } from './autoreply.js';
import { validatePattern } from './patterns.js';

const pattern = z
  .string()
  .trim()
  .min(1)
  .superRefine((value, ctx) => {
    const err = validatePattern(value);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Невалидный паттерн: ${err}` });
  });

export const patternGroupSchema = z.object({
  key: z.string().trim().min(1).max(64),
  title: z.string().trim().min(1).max(120),
  enabled: z.boolean(),
  /** Словарь для тех, кто не пишет regex: слова и фразы целиком, `*` в конце — любое окончание. */
  keywords: z.array(z.string().trim().min(1)).default([]),
  patterns: z.array(pattern),
  /**
   * Только для групп вопросов: срабатывать и когда клиент отвечает на сообщение сотрудника
   * (по умолчанию такие ответы при clientReplyPolicy = ignore_to_staff вопросами не считаются).
   * Пример — «Обновите» в ответ на курс сотрудника.
   */
  evenInReply: z.boolean().optional(),
});
export type PatternGroup = z.infer<typeof patternGroupSchema>;

// ── Секции ───────────────────────────────────────────────────────────────────

export const generalSchema = z.object({
  timezone: z.string().min(1),
  boardRecentClosedMinutes: z.number().int().min(0).max(24 * 60),
});

export const trackingSchema = z.object({
  enabled: z.boolean(),
  chatTypes: z.array(z.enum(['group', 'supergroup', 'channel', 'private'])).min(1),
  /** Сообщения старше этого возраста не создают тикетов (бэкфилл, догонка после простоя). */
  maxMessageAgeSec: z.number().int().min(60).max(7 * 24 * 3600),
  pollIntervalMs: z.number().int().min(250).max(60_000),
  batchSize: z.number().int().min(10).max(5000),
});

export const detectionSchema = z.object({
  minClientTextLength: z.number().int().min(0).max(500),
  /** Как относиться к reply клиента: игнорировать любые, только ответы сотрудникам, или не игнорировать. */
  clientReplyPolicy: z.enum(['ignore_all', 'ignore_to_staff', 'allow']),
  /** Вложение без текста от клиента считаем запросом в эту колонку (null — не считаем). */
  attachmentColumnKey: z.string().nullable(),
  /** Текст, подходящий под любую включённую группу, — вопрос клиента. */
  question: z.array(patternGroupSchema),
  /** Текст клиента, подходящий под группу, — точно не вопрос (приветствие, логистика, шаблоны ботов). */
  ignore: z.array(patternGroupSchema),
  /** Сообщение сотрудника, подходящее под группу, — обещание вернуться, а не ответ. */
  promise: z.array(patternGroupSchema),
  /** Фиксация сделки в чате. */
  deal: z.array(patternGroupSchema),
});

export const timerLevelSchema = z.object({
  key: z.string().trim().min(1).max(32),
  label: z.string().trim().min(1).max(60),
  afterSec: z.number().int().min(1),
  color: z.string().trim().min(1).max(32),
});

export const timersSchema = z
  .object({
    levels: z.array(timerLevelSchema),
    abandonAfterSec: z.number().int().min(60),
    promiseTimeoutSec: z.number().int().min(10),
    sweepIntervalMs: z.number().int().min(1000).max(60_000),
  })
  .superRefine((v, ctx) => {
    const sorted = [...v.levels].sort((a, b) => a.afterSec - b.afterSec);
    if (sorted.some((l, i) => i > 0 && l.afterSec === sorted[i - 1]!.afterSec)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'У уровней не должно быть одинаковых порогов' });
    }
    if (new Set(v.levels.map((l) => l.key)).size !== v.levels.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ключи уровней должны быть уникальны' });
    }
  });

export const repliesSchema = z.object({
  /** Сотрудник с закреплёнными колонками закрывает только их; остальные — все колонки чата. */
  respectEmployeeColumns: z.boolean(),
  /** Сообщения босса закрывают запросы. */
  bossCloses: z.boolean(),
  /** Фикс в чате закрывает открытые запросы статусом «Сделка». */
  dealClosesTickets: z.boolean(),
  /**
   * Сотрудники, у которых ручное закрытие «Ответили» (кнопкой в карточке) засчитывается как их ответ в момент закрытия —
   * если своего ответа в чате у них по карточке нет. Пользователь трекера должен быть привязан к сотруднику.
   */
  manualCloseEmployeeIds: z.array(z.number().int().positive()).max(200).default([]),
});

export const groupOptionSchema = z.object({
  key: z.string().trim().min(1).max(16),
  label: z.string().trim().min(1).max(60),
});

export const groupsSchema = z.object({
  tradeTitleTemplate: z.string().min(1),
  internalTitleTemplate: z.string().min(1),
  departments: z.array(groupOptionSchema).min(1),
  categories: z.array(groupOptionSchema).min(1),
  postCreateMessages: z.array(z.string().trim().min(1)),
  autoDeleteSec: z.number().int().min(0),
  sendInviteLink: z.boolean(),
  pingExtraMembers: z.boolean(),
  /** Категория, которой помечается созданный чат: торговый → клиентский, внутренний → внутренний. */
  tradeChatCategory: z.string().nullable(),
  internalChatCategory: z.string().nullable(),
});

export const settingsSchemas = {
  general: generalSchema,
  tracking: trackingSchema,
  detection: detectionSchema,
  timers: timersSchema,
  replies: repliesSchema,
  groups: groupsSchema,
  autoreply: autoreplySchema,
} as const;

export type SettingsSection = keyof typeof settingsSchemas;
export const SETTINGS_SECTIONS = Object.keys(settingsSchemas) as SettingsSection[];
export type Settings = { [K in SettingsSection]: z.infer<(typeof settingsSchemas)[K]> };

export const SETTINGS_SECTION_LABEL: Record<SettingsSection, string> = {
  general: 'Общие',
  tracking: 'Трекинг',
  detection: 'Распознавание',
  timers: 'Таймеры',
  replies: 'Ответы',
  groups: 'Создание групп',
  autoreply: 'Автоответчик',
};

// ── Заводские значения ───────────────────────────────────────────────────────

export const DEFAULT_SETTINGS: Settings = {
  general: {
    timezone: 'Europe/Moscow',
    boardRecentClosedMinutes: 15,
  },
  tracking: {
    enabled: true,
    chatTypes: ['group', 'supergroup'],
    maxMessageAgeSec: 3600,
    pollIntervalMs: 1000,
    batchSize: 500,
  },
  detection: {
    minClientTextLength: 8,
    clientReplyPolicy: 'ignore_to_staff',
    attachmentColumnKey: 'cashless',
    question: [
      {
        key: 'rate',
        title: 'Курс и цена',
        enabled: true,
        keywords: [],
        patterns: [
          'курс|цена|почем|\\bпо\\s+чем\\b|тариф|подскажите.{0,20}курс|какой курс|какая цена|ставк',
          'сколько.{0,10}(юсдт|тез|доллар|синий|бак|евро|usdt)',
          // «Как покупаете?», «Тез как продаёте?», «Как купите 300к usdt?» — вопрос о курсе без слова «курс»
          '\\b(как|за\\s+сколько)\\b[^?\\n]{0,40}\\b(покупаете|продаете|продаёте|берете|берёте|забираете|отдаете|отдаёте|купите|продадите|покупаем|продаем|продаём|купим|продадим)\\b[^?\\n]{0,40}\\?',
        ],
      },
      {
        // «Обновите пожалуйста», «60к евро в тез обновите плиз» — часто ответом на курс сотрудника
        key: 'refresh',
        title: 'Обновить курс',
        enabled: true,
        evenInReply: true,
        keywords: [],
        patterns: ['\\bобнов(и|ите|им|ишь)\\b'],
      },
      {
        key: 'deal_intent',
        title: 'Купить / продать / обменять',
        enabled: true,
        keywords: [],
        patterns: [
          'куплю|продам|перестановк|берете|отдаете|принимаете',
          'нужен тез|нужен usdt|нужны баксы|нужен синий',
          '(мой|моя|мои|наш|наши).{0,12}(доллар|долл|бакс|usd|юсдт|usdt|тез|руб|синий|бел|евро|eur).{0,25}ваш',
          'ваш.{0,12}(доллар|долл|бакс|usd|юсдт|usdt|тез|руб|синий|бел|евро|eur).{0,25}(мой|моя|мои|наш|наши)',
          'принять.{0,15}(руб|тез|синий|usdt|евро)|выдать.{0,15}(руб|тез|синий)',
          'интересует.{0,20}(курс|сделк)|доступно.{0,10}(usdt|тез|синий)',
          'есть.{0,10}(тез|руб|синий|usdt|евро)\\b',
          'берете.{0,10}(руб|синий|баксы|евро)|продаете.{0,10}(тез|usdt)',
          'откуп|выкуп',
          // «могу купить синий?», «хочу продать 50000 син на ру», «купить $ 70 т»
          '\\b(купить|продать|обменять)\\b',
          // «Можете выдать?», «Когда примете ?», «Владивосток 2 кк выдать ?» — выдать/принять с вопросом, без валюты
          '\\b(выдать|принять|выдадите|примете|выда[её]те)\\b[^?]{0,25}\\?',
          // «поменять нужно 800.000₽ на USDT» — только с валютой («сумму поменять» — не запрос)
          '\\bпоменять\\b.{0,25}(синий|синие|бел|тез|usdt|юсдт|доллар|долл|бакс|евро|eur|руб|крипт)',
          // синий в вопросе: «Как синий прием?», «А за синий?», «3700 Син купите за ру ?» (без «?» — рабочая переписка)
          '\\bсин(ий|ие|их|его|ему|им|ь)?\\b[^?]{0,40}\\?',
          // клиент ждёт ответа: «Вы на связи ?», «Работаете уже ?»
          'на\\s+связи[\\s!.)]*\\?|работаете[^?]{0,20}\\?',
        ],
      },
    ],
    ignore: [
      {
        key: 'greeting',
        title: 'Только приветствие',
        enabled: true,
        keywords: [],
        patterns: ['^(доброе\\s+(утро|день|вечер)|привет|здравствуй|добрый\\s+(день|вечер)|приветствую|коллеги)[!.\\s]*$'],
      },
      {
        key: 'bot_templates',
        title: 'Шаблоны ботов и заявок',
        enabled: true,
        keywords: [],
        patterns: [
          '^Номер:\\s*\\d',
          '^/(пр|пт|пд)\\s+\\d',
          'Заявка:\\s*\\d{6,}',
          '^#(продам|куплю|покупаю|продаю)\\s*\\n.*лот',
        ],
      },
      {
        key: 'logistics',
        title: 'Логистика и курьеры',
        enabled: true,
        keywords: [],
        patterns: [
          'уже вышел|выехал|едет|где курьер|скоро будет|когда приедет',
          'можем в \\d|код такой|код тот же|актуально\\?$',
          'добыть там|встреча|забрать|доставить|привезти',
          'готово|принял|отправил|прибыл|ждите',
        ],
      },
    ],
    promise: [
      {
        key: 'promise',
        title: 'Обещание вернуться',
        enabled: true,
        keywords: [],
        patterns: [
          'вернусь|посчитаю|подожди|секунду|уточню|узнаю|подготовлю|минуту|одну минуту',
          'на\\s+связи|смотрю|считаю|сейчас\\s+уточн|в\\s+процессе|отвечу|дам\\s+ответ|сообщу',
        ],
      },
    ],
    deal: [
      {
        key: 'fix',
        title: 'Фикс сделки',
        enabled: true,
        keywords: [],
        patterns: ['(?<!\\w)(?:зафикс\\w*|фиксан\\w*|фиксиру\\w*|фикс\\b)'],
      },
    ],
  },
  timers: {
    levels: [
      { key: 'warn', label: 'Ждёт', afterSec: 60, color: '#e5a50a' },
      { key: 'warn2', label: 'Долго', afterSec: 180, color: '#e66100' },
      { key: 'escalate', label: 'Горит', afterSec: 300, color: '#e01b24' },
    ],
    abandonAfterSec: 3600,
    promiseTimeoutSec: 600,
    sweepIntervalMs: 5000,
  },
  replies: {
    respectEmployeeColumns: true,
    bossCloses: true,
    dealClosesTickets: true,
    manualCloseEmployeeIds: [],
  },
  groups: {
    tradeTitleTemplate: 'Bs / {name}',
    internalTitleTemplate: 'Bs / {name} #{dept}.{cat}',
    departments: [
      { key: 'INT', label: 'INT' },
      { key: 'TRD', label: 'TRD' },
    ],
    categories: [
      { key: 'BS', label: 'BS' },
      { key: 'FRS', label: 'Форсаж' },
      { key: 'KAS', label: 'Кассы' },
      { key: 'ZAY', label: 'Заявки' },
      { key: 'LOG', label: 'Логистика' },
      { key: 'FIN', label: 'Финансы' },
      { key: 'OPS', label: 'Операции' },
      { key: 'PRT', label: 'Партнёры' },
      { key: 'ADM', label: 'Админ' },
    ],
    postCreateMessages: ['/приветствие', '/контакты'],
    autoDeleteSec: 604800,
    sendInviteLink: true,
    pingExtraMembers: true,
    tradeChatCategory: 'client',
    internalChatCategory: 'internal',
  },
  autoreply: {
    enabled: false,
    weekdays: { from: '07:00', to: '19:00' },
    saturday: { mode: 'off', from: '10:00', to: '16:00' },
    sunday: { mode: 'off', from: '10:00', to: '16:00' },
    delaySec: 900,
    texts: ['Здравствуйте! Сейчас нерабочее время — мы ответим вам утром.'],
    dictionaries: { weekdayMorning: [], weekdayEvening: [], saturdayMorning: [], saturdayEvening: [], sunday: [] },
    replyToQuestion: true,
    missedColumnKey: null,
    offHoursTimerFromWorkStart: false,
    skipBots: true,
    columnSchedules: [],
  },
};

/** Подставляет значения в шаблон названия группы. `_` в имени заменяется пробелом, как в прототипе. */
export function buildGroupTitle(template: string, vars: { name: string; dept?: string; cat?: string }): string {
  return template
    .replace(/\{name\}/g, vars.name.trim())
    .replace(/\{dept\}/g, vars.dept ?? '')
    .replace(/\{cat\}/g, vars.cat ?? '')
    .replace(/_/g, ' ')
    .trim();
}
